import { MediaContent, MediaContentResponse } from '@goldcast/api/content';
import { isNumber } from 'radash';
import { getFallbackFPS } from './fpsUtils';

/**
 * Check whether this is an Event
 * @param media_source_type MediaSourceTypeEnum
 * @param event_type EventTypeEnum
 * @returns boolean
 */
export const isEventRecording = (
  media_source_type: MediaContentResponse['media_source_type'],
  event_type: MediaContentResponse['event_type']
): boolean => {
  return media_source_type === 'RECORDING' && event_type !== 'Recording';
};

export const isUploadedVideo = (media_source_type: MediaContentResponse['media_source_type']): boolean => {
  return media_source_type === 'UPLOAD';
};

/**
 * Check whether event was recorded from Recording studio
 * @param event_type EventTypeEnum
 * @returns boolean
 */
export const isEventTypeRecording = (event_type: MediaContentResponse['event_type']) => {
  return event_type === 'Recording';
};

export const getContentFPS = (content: MediaContentResponse): number => {
  if (isNumber(content.content_fps)) {
    return content.content_fps;
  }
  return getFallbackFPS();
};

/**
 * Checks whether the event is a live event recording
 * @param media_source_type MediaSourceTypeEnum
 * @param event_type EventTypeEnum
 * @param broadcast_type BroadcastType5ccEnum
 * @returns boolean
 */
export const isLiveEventRecording = (
  media_source_type: MediaContentResponse['media_source_type'],
  event_type: MediaContentResponse['event_type'],
  broadcast_type: MediaContentResponse['broadcast_type']
) => {
  return isEventRecording(media_source_type, event_type) && broadcast_type === 'LIVE_HYBRID';
};

/**
 * Checks whether the event is a pre-recorded event
 * @param media_source_type MediaSourceTypeEnum
 * @param event_type EventTypeEnum
 * @param broadcast_type BroadcastType5ccEnum
 * @returns boolean
 */
export const isPreRecordedEvent = (
  media_source_type: MediaContentResponse['media_source_type'],
  event_type: MediaContentResponse['event_type'],
  broadcast_type: MediaContentResponse['broadcast_type']
) => {
  return (
    isEventRecording(media_source_type, event_type) &&
    !isLiveEventRecording(media_source_type, event_type, broadcast_type)
  );
};

/**
 * Checks whether the individual recording status is DONE
 * @param individual_recording_status IndividualRecordingStatusEnum
 * @returns boolean
 */
export const isAlignedStreamAvailable = (
  individual_recording_status: MediaContentResponse['individual_recording_status']
): boolean => {
  return individual_recording_status === 'DONE';
};

/**
 * Checks whether the individual recording status is PROCESSING
 * @param individual_recording_status IndividualRecordingStatusEnum
 * @returns boolean
 */
export const isAlignedStreamProcessing = (
  individual_recording_status: MediaContentResponse['individual_recording_status']
) => {
  return individual_recording_status === 'PROCESSING';
};

const getContentRemotionMigrationStatus = (content: MediaContent | MediaContentResponse) => {
  return content.remotion_migration_status;
};

export const isRemotionMigrationNotStarted = (content: MediaContent | MediaContentResponse) => {
  return getContentRemotionMigrationStatus(content) === 'NOT_STARTED';
};

export const isRemotionMigrationInProgress = (content: MediaContent | MediaContentResponse) => {
  return getContentRemotionMigrationStatus(content) === 'IN_PROGRESS';
};

export const isRemotionMigrationDone = (content: MediaContent | MediaContentResponse) => {
  return getContentRemotionMigrationStatus(content) === 'DONE';
};

/**
 * Old live event
 * @param content
 * @returns
 */
export const isLiveEventWithManualMigration = (content: MediaContent | MediaContentResponse) => {
  return content.user_triggered_migration;
};

/**
 * New live event
 * @param content
 * @returns
 */
export const isLiveEventWithAutoMigration = (content: MediaContent | MediaContentResponse) => {
  return !isLiveEventWithManualMigration(content);
};

export const getContentBroadcastId = (content: MediaContent | MediaContentResponse) => {
  return content.id;
};

export const isRemotionMigrationPossibleForContent = (content: MediaContent | MediaContentResponse) => {
  return content.remotion_migration_enabled;
};

/**
 * Return true if we've mp4 available (be it preview or source)
 * Which is mandatory requirement for remotion usage
 * @param content MediaContent or MediaContentResponse
 * @returns boolean
 */
export const isContentCompositeMp4Available = (content: MediaContent | MediaContentResponse) => {
  return content.preview_available;
};

export const isAudioContent = (content: MediaContent | MediaContentResponse) => {
  return content?.media_type === 'AUDIO';
};
export const isNotFullLengthSession = ({
  startTime,
  endTime,
  maxVideoDuration
}: {
  startTime?: number;
  endTime?: number;
  maxVideoDuration?: number;
}) => {
  if (
    typeof startTime !== 'number' ||
    typeof endTime !== 'number' ||
    typeof maxVideoDuration !== 'number' ||
    !Number.isFinite(startTime) ||
    !Number.isFinite(endTime) ||
    !Number.isFinite(maxVideoDuration)
  ) {
    return false;
  }

  return startTime > 0 || endTime < maxVideoDuration;
};
export const isLayoutsWithVisionDataEnabledForSession = ({
  contentMediaSource,
  eventType,
  broadcastType,
  startTime,
  endTime,
  maxVideoDuration,
  areLayoutsEnabledForPreRecordedEvents
}: {
  contentMediaSource?: MediaContentResponse['media_source_type'];
  eventType?: MediaContentResponse['event_type'] | null;
  broadcastType?: MediaContentResponse['broadcast_type'];
  startTime?: number;
  endTime?: number;
  maxVideoDuration?: number;
  areLayoutsEnabledForPreRecordedEvents: boolean;
}) => {
  const isCustomUpload = contentMediaSource === 'UPLOAD';
  const isPreRecordedWithLayoutsEnabled =
    !!contentMediaSource &&
    !!broadcastType &&
    isPreRecordedEvent(contentMediaSource, eventType ?? undefined, broadcastType) &&
    isNotFullLengthSession({ startTime, endTime, maxVideoDuration }) &&
    areLayoutsEnabledForPreRecordedEvents;

  return isCustomUpload || isPreRecordedWithLayoutsEnabled;
};
